// utils/careerData.ts
// Full career paths data model — production version
// All data in Hebrew, RTL-ready

// ─── Types ─────────────────────────────────────────────────────────────────────

export type SkillLevel = 'beginner' | 'intermediate' | 'advanced';
export type DemandType = 'hot' | 'growing' | 'stable';
export type CategoryType = 'software' | 'data' | 'design' | 'marketing' | 'business' | 'cyber';

export interface CareerSkill {
  name: string;
  level: SkillLevel;
  required: boolean;
}

export interface SalaryBand {
  label: string;       // "Junior", "Mid", "Senior"
  min: number;         // monthly ILS
  max: number;
  experience: string;  // "0-2 שנים"
}

export interface RoadmapStage {
  id: string;
  number: number;
  title: string;
  description: string;
  duration: string;    // "4-6 שבועות"
  topics: string[];
  outcomes: string[];
}

export interface Career {
  slug: string;
  title: string;
  shortTitle: string;
  emoji: string;
  category: CategoryType;
  demand: DemandType;
  description: string;
  longDescription: string;
  salaryMin: number;       // monthly ILS
  salaryMax: number;
  salaryBands: SalaryBand[];
  timeToLearn: string;     // "4-8 חודשים"
  avgMonthlyJobs: number;
  jobGrowthRate: number;   // % per year
  skills: CareerSkill[];
  roadmap: RoadmapStage[];
  topCompanies: string[];
  pros: string[];
  cons: string[];
  bestFor: string[];
  catalogTopic: string;
  chatSeed: string;
  relatedSlugs: string[];
}

// ─── Career Definitions ────────────────────────────────────────────────────────

export const CAREERS: Career[] = [

  // ── 1. Frontend Developer ──────────────────────────────────────────────────
  {
    slug: 'frontend-developer',
    title: 'מפתח Front-End',
    shortTitle: 'Front-End',
    emoji: '💻',
    category: 'software',
    demand: 'hot',
    description: 'בניית ממשקי משתמש מרהיבים וחוויות אינטראקטיביות שמשתמשים אוהבים.',
    longDescription:
      'מפתחי Front-End הם האנשים שהופכים עיצובים לממשקים חיים. הם עובדים עם HTML, CSS ו-JavaScript כדי לבנות את החלק הנראה לעין של כל אפליקציה ואתר. בשוק הישראלי, React היא הטכנולוגיה הדומיננטית, ועם שילוב TypeScript הביקוש למפתחי Front-End מיומנים נמצא בשיא.',
    salaryMin: 18000,
    salaryMax: 38000,
    salaryBands: [
      { label: 'Junior', min: 18000, max: 24000, experience: '0–2 שנים' },
      { label: 'Mid',    min: 24000, max: 32000, experience: '2–4 שנים' },
      { label: 'Senior', min: 32000, max: 38000, experience: '4+ שנים' },
    ],
    timeToLearn: '4–8 חודשים',
    avgMonthlyJobs: 280,
    jobGrowthRate: 22,
    skills: [
      { name: 'HTML & CSS',        level: 'beginner',      required: true  },
      { name: 'JavaScript',        level: 'intermediate',  required: true  },
      { name: 'React',             level: 'intermediate',  required: true  },
      { name: 'TypeScript',        level: 'intermediate',  required: false },
      { name: 'Git & GitHub',      level: 'beginner',      required: true  },
      { name: 'Responsive Design', level: 'beginner',      required: true  },
      { name: 'REST APIs',         level: 'intermediate',  required: false },
      { name: 'Testing (Jest)',    level: 'intermediate',  required: false },
    ],
    roadmap: [
      {
        id: 'fe-1', number: 1,
        title: 'יסודות Web',
        description: 'HTML5 סמנטי, CSS3, Flexbox, Grid, Responsive Design',
        duration: '4–6 שבועות',
        topics: ['HTML5 סמנטי', 'CSS3 ו-Flexbox', 'CSS Grid', 'Mobile-First Design', 'Browser DevTools'],
        outcomes: ['בניית דפי נחיתה עצמאית', 'עיצוב רספונסיבי', 'הבנת DOM'],
      },
      {
        id: 'fe-2', number: 2,
        title: 'JavaScript מודרני',
        description: 'ES6+, DOM Manipulation, Async/Await, Fetch API',
        duration: '6–8 שבועות',
        topics: ['Variables, Functions, Arrays', 'DOM Events', 'Promises & Async', 'Fetch API & REST', 'Local Storage'],
        outcomes: ['כתיבת לוגיקה צד לקוח', 'אינטגרציה עם APIs', 'ניהול מצב בסיסי'],
      },
      {
        id: 'fe-3', number: 3,
        title: 'React & Ecosystem',
        description: 'Component-based, Hooks, State Management, Routing',
        duration: '8–10 שבועות',
        topics: ['JSX ו-Components', 'useState & useEffect', 'React Router', 'Context API', 'React Query'],
        outcomes: ['בניית SPAs מלאות', 'ניהול state מרכזי', 'routing בין דפים'],
      },
      {
        id: 'fe-4', number: 4,
        title: 'TypeScript & Tooling',
        description: 'Type safety, Testing, Performance, Build tools',
        duration: '4–6 שבועות',
        topics: ['TypeScript Basics', 'Jest & Testing Library', 'Webpack/Vite', 'Git Workflow', 'CI/CD Basics'],
        outcomes: ['קוד מוקלד ובטוח', 'Testing בסיסי', 'עבודה בצוות'],
      },
      {
        id: 'fe-5', number: 5,
        title: 'פרויקט ופורטפוליו',
        description: '2-3 פרויקטים לפורטפוליו + הכנה לראיונות טכניים',
        duration: '4–6 שבועות',
        topics: ['פרויקט E-commerce', 'פרויקט Dashboard', 'GitHub Pages', 'LinkedIn Profile', 'Technical Interviews'],
        outcomes: ['פורטפוליו מרשים', 'ביטחון בראיונות', 'מוכנות לשוק'],
      },
    ],
    topCompanies: ['Wix', 'Monday.com', 'Fiverr', 'IronSource', 'Gett', 'Riskified', 'Via'],
    pros: ['ביקוש גבוה מאוד', 'כניסה מהירה לשוק', 'עבודה יצירתית', 'אפשרות לעבוד Remote', 'פורטפוליו נראה לעין'],
    cons: ['הטכנולוגיות משתנות מהר', 'תחרות גבוהה בג\'וניורים', 'לפעמים שכר נמוך מ-Backend', 'דורש עיניים לפרטים'],
    bestFor: ['אנשים שאוהבים לעצב', 'מי שרוצה תוצאות נראות לעין', 'כניסה מהירה לעולם הטכנולוגיה'],
    catalogTopic: 'תכנות',
    chatSeed: 'אני רוצה להיות מפתח Front-End',
    relatedSlugs: ['fullstack-developer', 'ux-ui-designer', 'devops-engineer'],
  },

  // ── 2. Full-Stack Developer ────────────────────────────────────────────────
  {
    slug: 'fullstack-developer',
    title: 'מפתח Full-Stack',
    shortTitle: 'Full-Stack',
    emoji: '🚀',
    category: 'software',
    demand: 'hot',
    description: 'שליטה מלאה ב-Frontend ו-Backend — הכוח להקים מוצרים מאפס לסוף.',
    longDescription:
      'Full-Stack Developer הוא המפתח המגוון ביותר בשוק. הוא יכול לבנות גם את הממשק וגם את השרת, לטפל בבסיסי נתונים ולהשיק אפליקציות עצמאית. בישראל, Full-Stack עם React+Node הוא ה-Stack הפופולרי ביותר בסטארטאפים ובחברות מוצר.',
    salaryMin: 22000,
    salaryMax: 48000,
    salaryBands: [
      { label: 'Junior', min: 22000, max: 28000, experience: '0–2 שנים' },
      { label: 'Mid',    min: 28000, max: 38000, experience: '2–5 שנים' },
      { label: 'Senior', min: 38000, max: 48000, experience: '5+ שנים' },
    ],
    timeToLearn: '8–14 חודשים',
    avgMonthlyJobs: 200,
    jobGrowthRate: 25,
    skills: [
      { name: 'JavaScript / TypeScript', level: 'intermediate', required: true  },
      { name: 'React / Next.js',         level: 'intermediate', required: true  },
      { name: 'Node.js & Express',       level: 'intermediate', required: true  },
      { name: 'SQL & PostgreSQL',        level: 'intermediate', required: true  },
      { name: 'REST APIs & GraphQL',     level: 'intermediate', required: false },
      { name: 'Docker Basics',           level: 'beginner',     required: false },
      { name: 'AWS / Cloud',             level: 'beginner',     required: false },
      { name: 'Auth & Security',         level: 'intermediate', required: false },
    ],
    roadmap: [
      {
        id: 'fs-1', number: 1,
        title: 'Front-End Foundations',
        description: 'HTML, CSS, JavaScript ES6+, React',
        duration: '8–10 שבועות',
        topics: ['HTML & CSS מתקדם', 'JavaScript ES6+', 'React Hooks', 'State Management', 'React Router'],
        outcomes: ['בניית UI מלאה', 'אינטגרציה עם APIs'],
      },
      {
        id: 'fs-2', number: 2,
        title: 'Back-End עם Node.js',
        description: 'Server, Routing, Middleware, REST APIs מאובטחות',
        duration: '8–10 שבועות',
        topics: ['Node.js ו-Express', 'REST API Design', 'Authentication (JWT)', 'Middleware & Error Handling', 'Validation'],
        outcomes: ['שרת מלא ו-REST API מאובטח'],
      },
      {
        id: 'fs-3', number: 3,
        title: 'Database & ORM',
        description: 'SQL, PostgreSQL, Prisma, Database Design',
        duration: '6–8 שבועות',
        topics: ['PostgreSQL & SQL', 'MongoDB Basics', 'Prisma ORM', 'Database Design', 'Migrations'],
        outcomes: ['עבודה עם DB', 'Queries מורכבות', 'Schema Design'],
      },
      {
        id: 'fs-4', number: 4,
        title: 'Next.js Full-Stack',
        description: 'SSR, App Router, Server Components, API Routes',
        duration: '6–8 שבועות',
        topics: ['Next.js App Router', 'Server Components', 'API Routes', 'Auth.js', 'Deployment'],
        outcomes: ['Full-Stack apps עם Next.js'],
      },
      {
        id: 'fs-5', number: 5,
        title: 'DevOps & Launch',
        description: 'Docker, CI/CD, Cloud, Monitoring',
        duration: '4–6 שבועות',
        topics: ['Docker & Docker Compose', 'GitHub Actions', 'Vercel / AWS', 'Monitoring', 'Performance'],
        outcomes: ['השקת פרויקטים לפרודקשן'],
      },
    ],
    topCompanies: ['Monday.com', 'Wix', 'Fiverr', 'Playtika', 'Amdocs', 'Check Point', 'Startup ecosystem'],
    pros: ['שכר גבוה', 'גמישות בעבודה', 'מתאים לפרילנס', 'ביקוש עצום', 'מגוון בעבודה'],
    cons: ['לוקח זמן רב ללמוד', 'צריך לעדכן ידע בשני תחומים', 'לפעמים surface-level'],
    bestFor: ['מי שרוצה שכר גבוה', 'יזמים שרוצים לבנות מוצרים', 'מי שאוהב גיוון'],
    catalogTopic: 'תכנות',
    chatSeed: 'אני רוצה להיות מפתח Full-Stack',
    relatedSlugs: ['frontend-developer', 'devops-engineer', 'data-scientist'],
  },

  // ── 3. Data Analyst ───────────────────────────────────────────────────────
  {
    slug: 'data-analyst',
    title: 'אנליסט דאטה',
    shortTitle: 'Data Analyst',
    emoji: '📊',
    category: 'data',
    demand: 'growing',
    description: 'הפיכת נתונים לתובנות עסקיות שמשנות החלטות ומניעות צמיחה.',
    longDescription:
      'אנליסטי דאטה הם המגשרים בין העולם הטכני לעסקי. הם אוספים, מנקים ומנתחים נתונים כדי לענות על שאלות עסקיות קריטיות. בישראל, כל חברה גדולה מחפשת אנליסטים שיכולים לדבר SQL ו-Excel ולהציג insights ברורים להנהלה.',
    salaryMin: 15000,
    salaryMax: 32000,
    salaryBands: [
      { label: 'Junior', min: 15000, max: 20000, experience: '0–2 שנים' },
      { label: 'Mid',    min: 20000, max: 26000, experience: '2–4 שנים' },
      { label: 'Senior', min: 26000, max: 32000, experience: '4+ שנים' },
    ],
    timeToLearn: '3–6 חודשים',
    avgMonthlyJobs: 180,
    jobGrowthRate: 18,
    skills: [
      { name: 'SQL',                level: 'intermediate', required: true  },
      { name: 'Excel מתקדם',        level: 'intermediate', required: true  },
      { name: 'Python בסיסי',       level: 'beginner',     required: false },
      { name: 'Power BI / Tableau', level: 'intermediate', required: true  },
      { name: 'סטטיסטיקה',          level: 'beginner',     required: true  },
      { name: 'Google Analytics',   level: 'beginner',     required: false },
      { name: 'Looker Studio',      level: 'beginner',     required: false },
    ],
    roadmap: [
      {
        id: 'da-1', number: 1,
        title: 'Excel & Spreadsheets',
        description: 'Pivot Tables, VLOOKUP, Power Query, Dashboards',
        duration: '3–4 שבועות',
        topics: ['Formulas מתקדמות', 'Pivot Tables', 'Power Query', 'Charts & Visualizations'],
        outcomes: ['ניתוח נתונים בסיסי', 'דשבורד ב-Excel'],
      },
      {
        id: 'da-2', number: 2,
        title: 'SQL לאנליזה',
        description: 'Queries, JOINs, Aggregations, Window Functions',
        duration: '5–7 שבועות',
        topics: ['SELECT & WHERE', 'JOINs', 'GROUP BY & Aggregations', 'Subqueries', 'Window Functions'],
        outcomes: ['שאילתות מורכבות', 'ניתוח DB עצמאי'],
      },
      {
        id: 'da-3', number: 3,
        title: 'Visualization & BI',
        description: 'Power BI / Tableau — דשבורדים מקצועיים',
        duration: '5–7 שבועות',
        topics: ['Power BI Desktop', 'DAX Basics', 'Tableau Basics', 'Dashboard Design', 'Storytelling עם דאטה'],
        outcomes: ['דשבורדים מקצועיים', 'הצגת תובנות להנהלה'],
      },
      {
        id: 'da-4', number: 4,
        title: 'Python לאנליזה',
        description: 'Pandas, NumPy, Matplotlib, EDA',
        duration: '5–7 שבועות',
        topics: ['Python Basics', 'Pandas ו-NumPy', 'Matplotlib ו-Seaborn', 'EDA Workflow', 'Data Cleaning'],
        outcomes: ['ניתוח אוטומטי', 'Python scripts לאנליזה'],
      },
    ],
    topCompanies: ['Bank Hapoalim', 'Wix Analytics', 'Fiverr', 'ECI Telecom', 'Check Point', 'Taboola'],
    pros: ['כניסה מהירה לשוק', 'עבודה בכל סקטור', 'ביקוש יציב', 'משכורות טובות', 'טכניקה + עסקים'],
    cons: ['Ceiling שכר נמוך מ-Engineer', 'הרבה ניקיון נתונים', 'פחות "sexy" מ-ML'],
    bestFor: ['מי שאוהב מספרים', 'אנשים מעולם העסקים שרוצים לעבור לטק', 'מי שרוצה כניסה מהירה'],
    catalogTopic: 'דאטה',
    chatSeed: 'אני רוצה להיות אנליסט דאטה',
    relatedSlugs: ['data-scientist', 'ai-ml-engineer', 'product-manager'],
  },

  // ── 4. AI/ML Engineer ─────────────────────────────────────────────────────
  {
    slug: 'ai-ml-engineer',
    title: 'מהנדס AI/ML',
    shortTitle: 'AI Engineer',
    emoji: '🤖',
    category: 'data',
    demand: 'hot',
    description: 'בניית מודלים חכמים שלומדים, צומחים ומשנים תעשיות שלמות.',
    longDescription:
      'מהנדסי AI/ML הם בין המבוקשים ביותר בעולם הטכנולוגיה. הם מפתחים אלגוריתמים שמאפשרים למחשבים ללמוד ולקבל החלטות. בעידן ה-LLMs וה-Generative AI, הביקוש לאנשי AI עלה ב-300% בשלוש שנים אחרונות.',
    salaryMin: 25000,
    salaryMax: 58000,
    salaryBands: [
      { label: 'Junior ML',    min: 25000, max: 33000, experience: '0–2 שנים' },
      { label: 'ML Engineer',  min: 33000, max: 45000, experience: '2–5 שנים' },
      { label: 'Senior / Lead',min: 45000, max: 58000, experience: '5+ שנים' },
    ],
    timeToLearn: '10–18 חודשים',
    avgMonthlyJobs: 90,
    jobGrowthRate: 38,
    skills: [
      { name: 'Python מתקדם',          level: 'advanced',      required: true  },
      { name: 'TensorFlow / PyTorch',   level: 'intermediate',  required: true  },
      { name: 'סטטיסטיקה ומתמטיקה',    level: 'intermediate',  required: true  },
      { name: 'SQL & Spark',            level: 'intermediate',  required: false },
      { name: 'NLP & Transformers',     level: 'intermediate',  required: false },
      { name: 'Computer Vision',        level: 'intermediate',  required: false },
      { name: 'MLOps & Deployment',     level: 'intermediate',  required: false },
      { name: 'LLM Fine-tuning',        level: 'advanced',      required: false },
    ],
    roadmap: [
      {
        id: 'ai-1', number: 1,
        title: 'Python & Math',
        description: 'Python מתקדם, Linear Algebra, Statistics',
        duration: '6–8 שבועות',
        topics: ['Python OOP', 'NumPy ו-Pandas', 'Matplotlib', 'Linear Algebra', 'Probability & Statistics'],
        outcomes: ['שליטה ב-Python לדאטה', 'הבנה מתמטית בסיסית'],
      },
      {
        id: 'ai-2', number: 2,
        title: 'Machine Learning',
        description: 'Supervised & Unsupervised Learning עם Scikit-learn',
        duration: '8–10 שבועות',
        topics: ['Linear/Logistic Regression', 'Decision Trees & Random Forest', 'SVM', 'Clustering', 'Model Evaluation'],
        outcomes: ['אימון מודלים בסיסיים', 'הבנת ML Pipeline'],
      },
      {
        id: 'ai-3', number: 3,
        title: 'Deep Learning',
        description: 'Neural Networks, CNNs, RNNs, Transformers',
        duration: '10–12 שבועות',
        topics: ['Neural Networks', 'PyTorch', 'CNNs לתמונות', 'RNNs ל-Sequences', 'Transformers & Attention'],
        outcomes: ['מודלים עמוקים', 'Computer Vision ו-NLP בסיסי'],
      },
      {
        id: 'ai-4', number: 4,
        title: 'LLMs & Generative AI',
        description: 'Fine-tuning, RAG, AI Agents',
        duration: '8–10 שבועות',
        topics: ['Hugging Face', 'Fine-tuning Models', 'RAG Systems', 'LangChain', 'AI Agents'],
        outcomes: ['בניית AI applications', 'Fine-tuning לצרכים ספציפיים'],
      },
      {
        id: 'ai-5', number: 5,
        title: 'MLOps & Production',
        description: 'Deployment, Monitoring, Scaling',
        duration: '6–8 שבועות',
        topics: ['Docker for ML', 'FastAPI', 'MLflow', 'Model Monitoring', 'Cloud (SageMaker/Vertex)'],
        outcomes: ['מודלים בפרודקשן', 'ניטור ותחזוקה'],
      },
    ],
    topCompanies: ['NVIDIA', 'Google Israel', 'Meta AI', 'Microsoft', 'Intel', 'AI21 Labs', 'Run.ai'],
    pros: ['השכר הגבוה ביותר בהייטק', 'עולם מרתק', 'ביקוש עולמי', 'אפשרויות Research'],
    cons: ['קשה ללא מתמטיקה', 'לוקח זמן רב', 'תחרות גבוהה', 'דורש לימוד מתמיד'],
    bestFor: ['בעלי רקע מתמטי/הנדסי', 'מי שרוצה השכר הגבוה ביותר', 'אוהבי מחקר ופיתוח'],
    catalogTopic: 'בינה מלאכותית',
    chatSeed: 'אני רוצה להיות מהנדס AI',
    relatedSlugs: ['data-scientist', 'data-analyst', 'devops-engineer'],
  },

  // ── 5. Cybersecurity Analyst ──────────────────────────────────────────────
  {
    slug: 'cybersecurity-analyst',
    title: 'מנתח סייבר',
    shortTitle: 'Cyber',
    emoji: '🛡️',
    category: 'cyber',
    demand: 'hot',
    description: 'הגנה על ארגונים מפני איומים דיגיטליים — שדה קרב אמיתי.',
    longDescription:
      'ישראל היא מעצמת סייבר עולמית. מנתחי סייבר עובדים ב-SOC, מגיבים לאיומים ומאבטחים תשתיות קריטיות. עם הסבה ממיחידות 8200 ו-81 ועם הביקוש הגובר של המגזר הפרטי, מסלול הסייבר הוא אחד הפופולריים ביותר בישראל.',
    salaryMin: 18000,
    salaryMax: 45000,
    salaryBands: [
      { label: 'Junior SOC',    min: 18000, max: 24000, experience: '0–2 שנים' },
      { label: 'Mid Analyst',   min: 24000, max: 35000, experience: '2–4 שנים' },
      { label: 'Senior / Lead', min: 35000, max: 45000, experience: '4+ שנים' },
    ],
    timeToLearn: '6–12 חודשים',
    avgMonthlyJobs: 140,
    jobGrowthRate: 30,
    skills: [
      { name: 'Networking & Protocols',  level: 'intermediate', required: true  },
      { name: 'Linux מתקדם',             level: 'intermediate', required: true  },
      { name: 'SIEM (Splunk/QRadar)',    level: 'intermediate', required: true  },
      { name: 'Incident Response',       level: 'intermediate', required: true  },
      { name: 'Penetration Testing',     level: 'intermediate', required: false },
      { name: 'Python לאוטומציה',        level: 'beginner',     required: false },
      { name: 'CompTIA Security+',       level: 'beginner',     required: false },
      { name: 'Cloud Security',          level: 'intermediate', required: false },
    ],
    roadmap: [
      {
        id: 'cy-1', number: 1,
        title: 'Networking Foundations',
        description: 'TCP/IP, Protocols, Firewalls, DNS, Wireshark',
        duration: '5–7 שבועות',
        topics: ['OSI Model', 'TCP/IP', 'DNS, DHCP, HTTP', 'Firewalls & NAT', 'Wireshark'],
        outcomes: ['הבנת רשתות', 'ניתוח תעבורת רשת'],
      },
      {
        id: 'cy-2', number: 2,
        title: 'Linux & Scripting',
        description: 'Linux Administration, Bash, Python בסיסי',
        duration: '5–7 שבועות',
        topics: ['Linux Commands', 'File Permissions', 'Bash Scripting', 'Python בסיסי', 'Regex'],
        outcomes: ['עבודה ב-Linux', 'אוטומציה בסיסית'],
      },
      {
        id: 'cy-3', number: 3,
        title: 'SOC Operations',
        description: 'SIEM, Log Analysis, Threat Detection, Incident Response',
        duration: '8–10 שבועות',
        topics: ['Splunk/QRadar', 'Log Analysis', 'MITRE ATT&CK', 'Alert Triage', 'Incident Response'],
        outcomes: ['עבודה ב-SOC', 'תגובה לאירועים'],
      },
      {
        id: 'cy-4', number: 4,
        title: 'Ethical Hacking',
        description: 'Kali Linux, OWASP, Vulnerability Assessment, CTFs',
        duration: '8–10 שבועות',
        topics: ['Kali Linux', 'OWASP Top 10', 'Metasploit', 'Burp Suite', 'CTF Challenges'],
        outcomes: ['בדיקות חדירה בסיסיות', 'הבנת אופן תקיפה'],
      },
    ],
    topCompanies: ['Check Point', 'CyberArk', 'Radware', 'Palo Alto', 'Rafael', 'Elbit', 'IDF Tech Units'],
    pros: ['ביקוש עצום בישראל', 'שכר גבוה מהר', 'עבודה מרגשת', 'אפשרויות ביטחוניות'],
    cons: ['לחץ גבוה', 'לפעמים On-call', 'דורש לימוד מתמיד', 'תחרות בצמרת'],
    bestFor: ['מי שאוהב אתגרים', 'בעלי רקע ביטחוני', 'מי שמתעניין ב-hacking חוקי'],
    catalogTopic: 'סייבר',
    chatSeed: 'אני רוצה להיות מנתח סייבר',
    relatedSlugs: ['devops-engineer', 'fullstack-developer', 'ai-ml-engineer'],
  },

  // ── 6. UX/UI Designer ─────────────────────────────────────────────────────
  {
    slug: 'ux-ui-designer',
    title: 'מעצב UX/UI',
    shortTitle: 'UX/UI',
    emoji: '🎨',
    category: 'design',
    demand: 'growing',
    description: 'עיצוב חוויות שמשתמשים אוהבים — שילוב אמנות, פסיכולוגיה וטכנולוגיה.',
    longDescription:
      'מעצבי UX/UI הם מי שגורמים למוצרים להרגיש נכון. הם חוקרים משתמשים, בונים Wireframes, יוצרים Prototypes ועובדים צמוד למפתחים. בישראל, הביקוש ל-Product Designers שמבינים גם UX וגם UI גדל כל הזמן.',
    salaryMin: 14000,
    salaryMax: 30000,
    salaryBands: [
      { label: 'Junior', min: 14000, max: 18000, experience: '0–2 שנים' },
      { label: 'Mid',    min: 18000, max: 24000, experience: '2–4 שנים' },
      { label: 'Senior', min: 24000, max: 30000, experience: '4+ שנים' },
    ],
    timeToLearn: '3–6 חודשים',
    avgMonthlyJobs: 110,
    jobGrowthRate: 16,
    skills: [
      { name: 'Figma',             level: 'intermediate', required: true  },
      { name: 'User Research',     level: 'beginner',     required: true  },
      { name: 'Wireframing',       level: 'beginner',     required: true  },
      { name: 'Prototyping',       level: 'intermediate', required: true  },
      { name: 'Design Systems',    level: 'intermediate', required: false },
      { name: 'Usability Testing', level: 'beginner',     required: false },
      { name: 'Motion Design',     level: 'beginner',     required: false },
    ],
    roadmap: [
      {
        id: 'ux-1', number: 1,
        title: 'UX Fundamentals',
        description: 'User Research, Personas, User Flows, Information Architecture',
        duration: '4–6 שבועות',
        topics: ['Design Thinking', 'User Personas', 'User Journeys', 'Information Architecture', 'Accessibility'],
        outcomes: ['חשיבה UX', 'יצירת Personas ו-Flows'],
      },
      {
        id: 'ux-2', number: 2,
        title: 'Figma & UI Design',
        description: 'Wireframes, Mockups, Design Systems, Auto Layout',
        duration: '6–8 שבועות',
        topics: ['Figma מתקדם', 'Typography', 'Color Theory', 'Components & Variants', 'Auto Layout'],
        outcomes: ['עיצוב ב-Figma', 'Design System בסיסי'],
      },
      {
        id: 'ux-3', number: 3,
        title: 'Prototyping & Testing',
        description: 'Interactive Prototypes, Usability Testing, A/B',
        duration: '4–6 שבועות',
        topics: ['Figma Prototyping', 'Usability Testing', 'A/B Testing', 'Heatmaps', 'Iterative Design'],
        outcomes: ['Prototypes אינטראקטיביים', 'Usability Studies'],
      },
      {
        id: 'ux-4', number: 4,
        title: 'Portfolio & Handoff',
        description: 'Case Studies, Developer Handoff, Job Prep',
        duration: '4–6 שבועות',
        topics: ['Case Studies Writing', 'Zeplin / Figma Handoff', 'Behance Portfolio', 'Interview Prep'],
        outcomes: ['פורטפוליו מרשים', 'מוכנות לשוק'],
      },
    ],
    topCompanies: ['Wix', 'Monday.com', 'Fiverr', 'Gett', 'AppsFlyer', 'Playtika', 'IronSource'],
    pros: ['יצירתי ומגוון', 'כניסה מהירה', 'עבודה רב-תחומית', 'Remote-friendly', 'פורטפוליו חזותי'],
    cons: ['שכר נמוך יחסית לפיתוח', 'תלות בטעם אישי', 'דורש ביקורת עצמית גבוהה'],
    bestFor: ['אנשים יצירתיים', 'מי שאוהב פסיכולוגיה ואנשים', 'שילוב אמנות ועסק'],
    catalogTopic: 'UI UX',
    chatSeed: 'אני רוצה להיות מעצב UX/UI',
    relatedSlugs: ['frontend-developer', 'product-manager', 'digital-marketer'],
  },

  // ── 7. Digital Marketer ───────────────────────────────────────────────────
  {
    slug: 'digital-marketer',
    title: 'מנהל שיווק דיגיטלי',
    shortTitle: 'שיווק דיגיטלי',
    emoji: '📱',
    category: 'marketing',
    demand: 'growing',
    description: 'הפיכת תקציב פרסום לצמיחה עסקית דרך Meta, Google, תוכן ו-SEO.',
    longDescription:
      'מנהלי שיווק דיגיטלי הם מי שמביאים לקוחות. הם מנהלים קמפיינים ב-Meta ו-Google, כותבים תוכן, מייעלים SEO ומנתחים נתונים. בישראל, כמעט כל עסק קטן ובינוני מחפש איש שיווק דיגיטלי מוכשר.',
    salaryMin: 12000,
    salaryMax: 28000,
    salaryBands: [
      { label: 'Junior',           min: 12000, max: 16000, experience: '0–1 שנים' },
      { label: 'Mid',              min: 16000, max: 22000, experience: '1–3 שנים' },
      { label: 'Senior / Manager', min: 22000, max: 28000, experience: '3+ שנים' },
    ],
    timeToLearn: '2–4 חודשים',
    avgMonthlyJobs: 220,
    jobGrowthRate: 14,
    skills: [
      { name: 'Meta Ads',          level: 'intermediate', required: true  },
      { name: 'Google Ads',        level: 'intermediate', required: true  },
      { name: 'SEO',               level: 'beginner',     required: true  },
      { name: 'Google Analytics 4',level: 'beginner',     required: true  },
      { name: 'Content Writing',   level: 'beginner',     required: false },
      { name: 'Email Marketing',   level: 'beginner',     required: false },
      { name: 'TikTok Ads',        level: 'beginner',     required: false },
      { name: 'CRM (HubSpot)',     level: 'beginner',     required: false },
    ],
    roadmap: [
      {
        id: 'dm-1', number: 1,
        title: 'Digital Marketing Basics',
        description: 'Funnels, Analytics, KPIs, UTM Tracking',
        duration: '2–3 שבועות',
        topics: ['Marketing Funnel', 'Digital Channels', 'Google Analytics 4', 'UTM Tracking', 'KPIs ומדדים'],
        outcomes: ['הבנת שיווק דיגיטלי', 'קריאת Analytics'],
      },
      {
        id: 'dm-2', number: 2,
        title: 'Social Ads',
        description: 'Meta Ads, TikTok, Instagram — קמפיינים ממירים',
        duration: '5–7 שבועות',
        topics: ['Meta Business Manager', 'Campaign Structure', 'Audiences & Targeting', 'Creative Best Practices', 'A/B Testing'],
        outcomes: ['קמפיינים ברווחיות', 'אופטימיזציה'],
      },
      {
        id: 'dm-3', number: 3,
        title: 'Google Ads & SEO',
        description: 'Search, Shopping, YouTube, SEO On/Off-Page',
        duration: '5–7 שבועות',
        topics: ['Google Search Ads', 'Keyword Research', 'SEO On-Page', 'SEO Off-Page', 'Google Shopping'],
        outcomes: ['קמפיינים ב-Google', 'SEO בסיסי'],
      },
      {
        id: 'dm-4', number: 4,
        title: 'Strategy & Portfolio',
        description: 'Full Funnel Strategy, Budget, Case Studies',
        duration: '3–4 שבועות',
        topics: ['Marketing Strategy', 'Budget Planning', 'Reporting', 'Case Studies', 'Portfolio Building'],
        outcomes: ['אסטרטגיה שיווקית מלאה', 'מוכן לשוק'],
      },
    ],
    topCompanies: ['Agencies', 'E-commerce Companies', 'Tech Startups', 'Media Companies', 'Freelance'],
    pros: ['כניסה מהירה לשוק', 'אפשרות פרילנס גבוהה', 'רלוונטי לכל תעשייה', 'יצירתי ומדיד'],
    cons: ['שכר נמוך ב-Entry', 'לחץ KPIs', 'שינויים מהירים בפלטפורמות', 'תלות בתקציב לקוח'],
    bestFor: ['אנשים תקשורתיים', 'מי שרוצה להיות עצמאי', 'שילוב מספרים ויצירתיות'],
    catalogTopic: 'שיווק דיגיטלי',
    chatSeed: 'אני רוצה להיות מנהל שיווק דיגיטלי',
    relatedSlugs: ['ux-ui-designer', 'product-manager', 'data-analyst'],
  },

  // ── 8. Product Manager ────────────────────────────────────────────────────
  {
    slug: 'product-manager',
    title: 'מנהל מוצר (PM)',
    shortTitle: 'Product Manager',
    emoji: '🧭',
    category: 'business',
    demand: 'growing',
    description: 'ניהול מוצר מרעיון לשוק — בין Business, Tech ו-Users.',
    longDescription:
      "מנהל מוצר הוא ה-CEO של הפיצ'ר. הוא מגדיר מה נבנה ולמה, עובד עם מפתחים, מעצבים ועסק, ומנהל את ה-Roadmap. בישראל, PM הוא אחת המשרות הנחשבות ביותר בהייטק עם נגישות גבוהה לאנשים שעברו מתחומים אחרים.",
    salaryMin: 20000,
    salaryMax: 48000,
    salaryBands: [
      { label: 'Associate PM',     min: 20000, max: 26000, experience: '0–2 שנים' },
      { label: 'PM',               min: 26000, max: 36000, experience: '2–5 שנים' },
      { label: 'Senior / Group PM',min: 36000, max: 48000, experience: '5+ שנים' },
    ],
    timeToLearn: '4–8 חודשים',
    avgMonthlyJobs: 95,
    jobGrowthRate: 20,
    skills: [
      { name: 'Product Strategy',       level: 'intermediate', required: true  },
      { name: 'User Research',          level: 'beginner',     required: true  },
      { name: 'Data Analysis',          level: 'beginner',     required: true  },
      { name: 'SQL בסיסי',              level: 'beginner',     required: false },
      { name: 'Agile / Scrum',          level: 'beginner',     required: true  },
      { name: 'Figma (קריאה)',          level: 'beginner',     required: false },
      { name: 'A/B Testing',            level: 'beginner',     required: false },
      { name: 'Stakeholder Management', level: 'intermediate', required: true  },
    ],
    roadmap: [
      {
        id: 'pm-1', number: 1,
        title: 'PM Fundamentals',
        description: 'Product Thinking, Frameworks, Discovery',
        duration: '4–6 שבועות',
        topics: ['Product Thinking', 'Jobs To Be Done', 'Customer Discovery', 'Problem vs Solution', 'PRD Writing'],
        outcomes: ['הבנת תפקיד PM', 'כתיבת PRD ראשוני'],
      },
      {
        id: 'pm-2', number: 2,
        title: 'Strategy & Roadmap',
        description: 'OKRs, Prioritization Frameworks, Roadmapping',
        duration: '5–7 שבועות',
        topics: ['OKRs ו-KPIs', 'Prioritization Frameworks', 'Roadmap Building', 'Stakeholder Alignment', 'Competitive Analysis'],
        outcomes: ['Roadmap רבעוני', "תיעדוף פיצ'רים"],
      },
      {
        id: 'pm-3', number: 3,
        title: 'Data & Analytics',
        description: 'Metrics, A/B Testing, SQL, Funnels',
        duration: '5–6 שבועות',
        topics: ['North Star Metric', 'Funnel Analysis', 'A/B Testing', 'SQL Basics', 'Mixpanel / Amplitude'],
        outcomes: ['קבלת החלטות מבוססת דאטה', 'ניתוח Funnel'],
      },
      {
        id: 'pm-4', number: 4,
        title: 'Agile & Execution',
        description: 'Scrum, Sprint Planning, Cross-functional Leadership',
        duration: '4–6 שבועות',
        topics: ['Scrum Ceremonies', 'Sprint Planning', 'User Stories', 'Cross-functional Leadership', 'Go-to-Market'],
        outcomes: ["ניהול Sprint", "השקת פיצ'רים"],
      },
    ],
    topCompanies: ['Monday.com', 'Wix', 'AppsFlyer', 'IronSource', 'Fiverr', 'Gett', 'Tipalti'],
    pros: ['שכר גבוה ויוקרה', 'עבודה מגוונת', 'השפעה אמיתית', 'קידום מהיר', 'Gateway לסטארטאפ'],
    cons: ['תחרות גבוהה', 'לחץ מכל הכיוונים', 'דורש ניסיון לעתים', 'Role מעורפל'],
    bestFor: ['בעלי ניסיון עסקי/טכנולוגי', 'אנשי תקשורת מצוינים', 'מי שאוהב להוביל'],
    catalogTopic: 'ניהול מוצר',
    chatSeed: 'אני רוצה להיות מנהל מוצר',
    relatedSlugs: ['data-analyst', 'ux-ui-designer', 'digital-marketer'],
  },

  // ── 9. DevOps Engineer ────────────────────────────────────────────────────
  {
    slug: 'devops-engineer',
    title: 'מהנדס DevOps',
    shortTitle: 'DevOps',
    emoji: '⚙️',
    category: 'software',
    demand: 'hot',
    description: 'חיבור בין פיתוח לתפעול — אוטומציה, ענן ו-CI/CD בקנה מידה.',
    longDescription:
      'מהנדסי DevOps גורמים לתוכנה לעבוד בפרודקשן בצורה אמינה ומהירה. הם בונים Pipeline של CI/CD, מנהלים Kubernetes clusters ומשיקים תשתיות ענן. בישראל, DevOps עם ניסיון ב-AWS ו-Kubernetes מאוד מבוקש.',
    salaryMin: 24000,
    salaryMax: 52000,
    salaryBands: [
      { label: 'Junior DevOps',    min: 24000, max: 30000, experience: '0–2 שנים' },
      { label: 'DevOps Engineer',  min: 30000, max: 42000, experience: '2–5 שנים' },
      { label: 'Senior / Platform',min: 42000, max: 52000, experience: '5+ שנים' },
    ],
    timeToLearn: '6–12 חודשים',
    avgMonthlyJobs: 110,
    jobGrowthRate: 32,
    skills: [
      { name: 'Linux מתקדם',              level: 'intermediate', required: true  },
      { name: 'Docker & Containers',       level: 'intermediate', required: true  },
      { name: 'Kubernetes',                level: 'intermediate', required: true  },
      { name: 'CI/CD (GitHub Actions)',    level: 'intermediate', required: true  },
      { name: 'AWS / Azure / GCP',         level: 'intermediate', required: true  },
      { name: 'Terraform (IaC)',           level: 'intermediate', required: false },
      { name: 'Python / Bash Scripting',   level: 'beginner',     required: false },
      { name: 'Monitoring (Prometheus)',   level: 'intermediate', required: false },
    ],
    roadmap: [
      {
        id: 'dv-1', number: 1,
        title: 'Linux & Networking',
        description: 'Linux Administration, Bash Scripting, Networking',
        duration: '5–7 שבועות',
        topics: ['Linux File System', 'Bash Scripting', 'Cron Jobs', 'Networking Basics', 'SSH & Security'],
        outcomes: ['שליטה ב-Linux', 'אוטומציה עם Bash'],
      },
      {
        id: 'dv-2', number: 2,
        title: 'Docker & Containers',
        description: 'Containerization, Images, Docker Compose',
        duration: '5–7 שבועות',
        topics: ['Docker Concepts', 'Dockerfile', 'Docker Compose', 'Container Networking', 'Registry'],
        outcomes: ['Containerize כל Application', 'Docker Compose Environments'],
      },
      {
        id: 'dv-3', number: 3,
        title: 'Kubernetes',
        description: 'Orchestration, Deployments, Services, Helm',
        duration: '7–9 שבועות',
        topics: ['Kubernetes Architecture', 'Pods & Deployments', 'Services & Ingress', 'Helm Charts', 'RBAC'],
        outcomes: ['ניהול Kubernetes Cluster', 'Helm Deployments'],
      },
      {
        id: 'dv-4', number: 4,
        title: 'CI/CD & Cloud',
        description: 'GitHub Actions, AWS, Terraform, GitOps',
        duration: '7–9 שבועות',
        topics: ['GitHub Actions', 'AWS Core Services', 'Terraform', 'GitOps', 'Secrets Management'],
        outcomes: ['Pipeline CI/CD מלא', 'Infrastructure as Code'],
      },
      {
        id: 'dv-5', number: 5,
        title: 'Monitoring & SRE',
        description: 'Observability, Alerting, Reliability Engineering',
        duration: '4–6 שבועות',
        topics: ['Prometheus & Grafana', 'ELK Stack', 'SLO / SLA', 'Incident Management', 'Chaos Engineering'],
        outcomes: ['Observability מלאה', 'ניטור פרודקשן'],
      },
    ],
    topCompanies: ['Check Point', 'WalkMe', 'Armis', 'BigPanda', 'Coralogix', 'Spot.io', 'Wiz'],
    pros: ['שכר גבוה מהר', 'ביקוש עצום', 'טכנולוגיות מגניבות', 'Remote-friendly', 'קריטי לכל חברה'],
    cons: ['On-call בלילות', 'לחץ גבוה ב-Incidents', 'לומדים תמיד', 'ידע רחב נדרש'],
    bestFor: ['מי שאוהב Infrastructure', 'אנשים מ-Linux Admin', 'מי שרוצה שכר גבוה'],
    catalogTopic: 'סייבר',
    chatSeed: 'אני רוצה להיות מהנדס DevOps',
    relatedSlugs: ['fullstack-developer', 'cybersecurity-analyst', 'ai-ml-engineer'],
  },

  // ── 10. Data Scientist ────────────────────────────────────────────────────
  {
    slug: 'data-scientist',
    title: 'מדען דאטה',
    shortTitle: 'Data Science',
    emoji: '🔬',
    category: 'data',
    demand: 'growing',
    description: 'מחקר עמוק בנתונים כדי לבנות מודלים Predictive שמשנים עסקים.',
    longDescription:
      'מדעני דאטה משלבים ניתוח סטטיסטי, Machine Learning ועסקים. הם בונים מודלים שמנבאים התנהגות משתמשים, מייעלים תהליכים ומוצאים patterns נסתרים. בשוק הישראלי, הכותרת "Data Scientist" דורשת Python ו-ML עמוק.',
    salaryMin: 22000,
    salaryMax: 50000,
    salaryBands: [
      { label: 'Junior DS',       min: 22000, max: 28000, experience: '0–2 שנים' },
      { label: 'Data Scientist',  min: 28000, max: 40000, experience: '2–5 שנים' },
      { label: 'Senior / Lead DS',min: 40000, max: 50000, experience: '5+ שנים' },
    ],
    timeToLearn: '10–16 חודשים',
    avgMonthlyJobs: 65,
    jobGrowthRate: 24,
    skills: [
      { name: 'Python (Pandas/NumPy)',  level: 'advanced',     required: true  },
      { name: 'Scikit-learn',           level: 'advanced',     required: true  },
      { name: 'SQL',                    level: 'intermediate', required: true  },
      { name: 'סטטיסטיקה',              level: 'advanced',     required: true  },
      { name: 'Jupyter Notebooks',      level: 'intermediate', required: true  },
      { name: 'Matplotlib / Seaborn',   level: 'intermediate', required: false },
      { name: 'Feature Engineering',    level: 'advanced',     required: false },
      { name: 'A/B Testing',            level: 'intermediate', required: false },
    ],
    roadmap: [
      {
        id: 'ds-1', number: 1,
        title: 'Math & Statistics',
        description: 'Linear Algebra, Calculus, Statistics, Probability',
        duration: '6–8 שבועות',
        topics: ['Linear Algebra', 'Calculus Basics', 'Statistics', 'Probability', 'Hypothesis Testing'],
        outcomes: ['בסיס מתמטי חזק', 'Statistical Testing'],
      },
      {
        id: 'ds-2', number: 2,
        title: 'Python for Data Science',
        description: 'Pandas, NumPy, EDA, Data Cleaning',
        duration: '6–8 שבועות',
        topics: ['Python OOP', 'Pandas מתקדם', 'NumPy', 'EDA Workflow', 'Data Cleaning'],
        outcomes: ['ניתוח דאטה מלא', 'EDA מקצועי'],
      },
      {
        id: 'ds-3', number: 3,
        title: 'Machine Learning',
        description: 'Supervised, Unsupervised, Feature Engineering',
        duration: '8–10 שבועות',
        topics: ['Regression & Classification', 'Tree Methods', 'Ensemble Methods', 'Cross-Validation', 'Feature Engineering'],
        outcomes: ['מודלים ML מלאים', 'ML Pipeline'],
      },
      {
        id: 'ds-4', number: 4,
        title: 'Advanced ML & Projects',
        description: 'Deep Learning, NLP, Time Series, Kaggle',
        duration: '8–10 שבועות',
        topics: ['Neural Networks', 'NLP Basics', 'Time Series', 'Kaggle Competitions', 'Storytelling עם דאטה'],
        outcomes: ['מודלים מתקדמים', 'Kaggle Experience'],
      },
      {
        id: 'ds-5', number: 5,
        title: 'Deployment & Portfolio',
        description: 'Streamlit, FastAPI, Hugging Face, Portfolio',
        duration: '4–6 שבועות',
        topics: ['Streamlit Apps', 'FastAPI', 'Hugging Face Spaces', 'Portfolio Projects', 'Interview Prep'],
        outcomes: ['DS Apps בפרודקשן', 'פורטפוליו מרשים'],
      },
    ],
    topCompanies: ['Google', 'Microsoft Israel', 'Amazon', 'Bank Hapoalim', 'Taboola', 'Outbrain', 'Mobileye'],
    pros: ['שכר גבוה', 'עבודה מחקרית מרתקת', 'ביקוש גדל', 'אפשרויות PhD / Research'],
    cons: ['נתיב ארוך', 'דורש מתמטיקה חזקה', '80% ניקיון דאטה', 'תחרות עם ML Engineers'],
    bestFor: ['בעלי רקע מתמטי/סטטיסטי', 'מי שאוהב מחקר', 'PhD holders שרוצים Industry'],
    catalogTopic: 'דאטה',
    chatSeed: 'אני רוצה להיות מדען דאטה',
    relatedSlugs: ['ai-ml-engineer', 'data-analyst', 'fullstack-developer'],
  },
];

// ─── Lookup Helpers ────────────────────────────────────────────────────────────

export const CATEGORY_LABELS: Record<CategoryType, string> = {
  software:  'פיתוח',
  data:      'דאטה ו-AI',
  design:    'עיצוב',
  marketing: 'שיווק',
  business:  'עסקים',
  cyber:     'סייבר',
};

export const CATEGORY_EMOJIS: Record<CategoryType, string> = {
  software:  '💻',
  data:      '📊',
  design:    '🎨',
  marketing: '📣',
  business:  '🧭',
  cyber:     '🛡️',
};

export const DEMAND_LABELS: Record<DemandType, string> = {
  hot:     '🔥 חם מאוד',
  growing: '📈 גדל',
  stable:  '✅ יציב',
};

export const DEMAND_COLORS: Record<DemandType, { bg: string; text: string; border: string }> = {
  hot:     { bg: 'rgba(239,68,68,0.10)',   text: '#DC2626', border: 'rgba(239,68,68,0.20)'   },
  growing: { bg: 'rgba(16,185,129,0.10)',  text: '#059669', border: 'rgba(16,185,129,0.20)'  },
  stable:  { bg: 'rgba(107,114,128,0.12)', text: '#4B5563', border: 'rgba(107,114,128,0.20)' },
};

export const CATEGORY_COLORS: Record<CategoryType, string> = {
  software:  '#3B82F6',
  data:      '#8B5CF6',
  design:    '#EC4899',
  marketing: '#F59E0B',
  business:  '#10B981',
  cyber:     '#EF4444',
};

export const SKILL_LEVEL_LABELS: Record<SkillLevel, string> = {
  beginner:     'מתחיל',
  intermediate: 'בינוני',
  advanced:     'מתקדם',
};

export const SKILL_LEVEL_COLORS: Record<SkillLevel, string> = {
  beginner:     '#10B981',
  intermediate: '#F59E0B',
  advanced:     '#EF4444',
};

// ─── Filter / Search ───────────────────────────────────────────────────────────

export function filterCareers(
  q: string,
  category: CategoryType | null,
  demand: DemandType | null,
): Career[] {
  const term = q.trim().toLowerCase();
  return CAREERS.filter((c) => {
    if (term) {
      const haystack = [
        c.title, c.shortTitle, c.description,
        ...c.skills.map((s) => s.name),
        ...c.pros,
        CATEGORY_LABELS[c.category],
      ].join(' ').toLowerCase();
      if (!haystack.includes(term)) return false;
    }
    if (category && c.category !== category) return false;
    if (demand  && c.demand   !== demand)   return false;
    return true;
  });
}

export function findCareer(slug: string): Career | undefined {
  return CAREERS.find((c) => c.slug === slug);
}

export function getRelatedCareers(career: Career): Career[] {
  return career.relatedSlugs
    .map((slug) => findCareer(slug))
    .filter(Boolean) as Career[];
}

export function formatSalary(amount: number): string {
  return `₪${(amount / 1000).toFixed(0)}K`;
}
